import React, { useRef, useState } from 'react';

import UploadIcon from '@mui/icons-material/Upload';

import { Button } from '@host/components/NewUI/ComponentsLibrary';

import { onboardColors } from '../../theme/onboardTokens';
import { font, palette } from '../styles';
import {
  partitionUploads,
  UPLOAD_ACCEPT,
  UPLOAD_FORMATS_HINT,
  UPLOAD_LIMITS_HINT
} from '../uploadRules';
import { usePortalCompact } from '../usePortalCompact';
import SelectedFiles, { fileKey } from './SelectedFiles';

interface Props {
  files?: File[];
  onFiles: (files: File[]) => void;
  onRemove?: (index: number) => void;
}

/**
 * Drag-and-drop / file-picker upload area for candidate documents. Files that
 * the server would reject (type, HEIC, size) are kept out and explained inline.
 * Once files are picked the area shows them as tiles and still accepts drops.
 */
const FileDropzone: React.FC<Props> = ({ files = [], onFiles, onRemove = () => {} }) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = useState(false);
  const [rejections, setRejections] = useState<string[]>([]);
  // Phones and tablets have no drag-and-drop, so only the picker button is offered.
  const compact = usePortalCompact();
  const filled = files.length > 0;

  const handleFiles = (list: FileList | null) => {
    if (!list) return;
    const { accepted, rejections: rejected } = partitionUploads(Array.from(list));
    setRejections(rejected);
    // Picking a file that is already in the list again would upload it twice.
    const picked = new Set(files.map(fileKey));
    const fresh = accepted.filter((file) => !picked.has(fileKey(file)));
    if (fresh.length) onFiles(fresh);
  };

  return (
    <>
      <div
        onDragLeave={() => setDragging(false)}
        onDragOver={(e) => {
          e.preventDefault();
          setDragging(true);
        }}
        onDrop={(e) => {
          e.preventDefault();
          setDragging(false);
          handleFiles(e.dataTransfer.files);
        }}
        style={{
          background: palette.uploadBg,
          border: dragging ? `2px dashed ${palette.tealDeep}` : `2px dashed transparent`,
          borderRadius: 8,
          padding: compact ? '24px 16px' : `${filled ? 34 : 32}px ${filled ? 48 : 32}px`,
          display: 'flex',
          flexDirection: 'column',
          alignItems: filled ? 'stretch' : 'center',
          gap: filled ? 24 : 8
        }}
      >
        {filled ? (
          <>
            <SelectedFiles {...{ files, onRemove }} />
            {/* Phones and keyboard users can't drop more files, so keep a picker. */}
            <div>
              <Button
                buttonVariant="outline"
                id="portal-add-more-files-button"
                onClick={() => inputRef.current?.click()}
                size="small"
              >
                Add more files
              </Button>
            </div>
          </>
        ) : (
          <>
            <div
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                justifyContent: 'center',
                alignItems: 'center',
                gap: 8
              }}
            >
              <UploadIcon sx={{ color: onboardColors.neutral.icon }} />
              {compact ? null : (
                <span style={{ color: palette.headingNeutral, fontFamily: font, fontSize: 14 }}>
                  Drag attachments here or
                </span>
              )}
              <Button
                buttonVariant="outline"
                id="portal-choose-file-button"
                onClick={() => inputRef.current?.click()}
                size="small"
              >
                Choose a file
              </Button>
            </div>
            <p style={{ color: palette.bodyNeutral, fontSize: 12, textAlign: 'center', margin: 0 }}>
              {UPLOAD_LIMITS_HINT}
              <br />
              {UPLOAD_FORMATS_HINT}
            </p>
          </>
        )}
        <input
          ref={inputRef}
          multiple
          accept={UPLOAD_ACCEPT}
          data-testid="portal-file-input"
          id="portal-file-input"
          style={{ display: 'none' }}
          type="file"
          onChange={(e) => {
            handleFiles(e.target.files);
            // Clear it so picking the same file again (after removing it) still fires.
            e.target.value = '';
          }}
        />
      </div>
      {rejections.length > 0 ? (
        <div role="alert" style={{ color: palette.warning, fontSize: 14, marginTop: 12 }}>
          {rejections.map((message) => (
            <p key={message} style={{ margin: '0 0 4px' }}>
              {message}
            </p>
          ))}
        </div>
      ) : null}
    </>
  );
};

export default FileDropzone;
